import { Avatar } from 'library/avatar';
import {
    ChatIcon,
    CrossIcon,
    DashboardIcon,
    DocumentIcon,
    PluginsIcon,
    ProgressLineIcon,
    UserManagementIcon,
} from 'library/icons';
import { MobileOrTabletMediaQuery, useDesktopMediaQuery } from 'library/media-query';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import useSidebarSelector from '../../hooks/useSidebarSelector';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { sidebarActions } from 'store/sidebar';
import { twMerge } from 'tailwind-merge';
import styles from './Sidebar.module.scss';
import clsx from 'clsx';

const SidebarLinks = [
    {
        to: '/dashboard',
        icon: <DashboardIcon />,
        label: 'Dashboard',
    },
    {
        to: '/users',
        icon: <UserManagementIcon />,
        label: 'Teams & Users',
    },
    {
        to: '/documents',
        icon: <DocumentIcon />,
        label: 'Documents',
    },
    {
        to: '/progress-line',
        icon: <ProgressLineIcon />,
        label: 'ProgressLine',
    },
    {
        to: '/plugins',
        icon: <PluginsIcon />,
        label: 'Plugins',
    },
    {
        to: '/support',
        icon: <ChatIcon />,
        label: 'Support',
    },
];

interface SidebarProps {}

const Sidebar: FC<SidebarProps> = (props) => {
    const { client } = useAuthSelector();

    const hasProPlan = client.plan._id.toLowerCase().includes('pro');

    const { show } = useSidebarSelector();

    const dispatch = useDispatch();

    const isDesktop = useDesktopMediaQuery();

    const showSidebar = useMemo(() => {
        if (isDesktop) return true;

        return show;
    }, [isDesktop, show]);

    const handleCloseSidebar = useCallback(() => {
        dispatch(sidebarActions.close());
    }, [dispatch]);

    return (
        <nav
            data-cy="sidebar"
            className={twMerge(
                styles['sidebar'],
                'fixed left-0 top-0 h-screen z-20 bg-card-color theme-custom:bg-transparent border-r border-separator-color-light hide-scrollbar',
                showSidebar ? 'translate-x-0' : 'translate-x-[-100vw]'
            )}
        >
            <div className="absolute inset-0 rounded-[inherit] hidden theme-custom:block theme-custom:bg-white/30 theme-custom:backdrop-blur-normal"></div>

            <div className="relative flex flex-col px-4 py-6 h-full">
                <div className="flex justify-between items-center">
                    <Link to="/dashboard" replace className={styles['sidebar-logo-wrapper']} data-cy="sidebar-logo">
                        <img className={styles['sidebar-logo']} src="/logos/logo.svg" alt="" />
                        <img className={styles['sidebar-logo-text']} src="/logos/logo-text.svg" alt="" />
                    </Link>

                    <MobileOrTabletMediaQuery>
                        <button
                            type="button"
                            className="btn-action"
                            onClick={handleCloseSidebar}
                            data-cy="sidebar-cross-btn"
                        >
                            <CrossIcon className="w-3 h-3 text-grayscale-500" />
                        </button>
                    </MobileOrTabletMediaQuery>
                </div>

                <ul className="mt-12 space-y-4">
                    {SidebarLinks.map((row, i) => (
                        <li key={i} data-cy={`sidebar-tab-${row.label.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`}>
                            <NavLink to={row.to} className={styles['sidebar-link']} onClick={handleCloseSidebar}>
                                <div>{row.icon}</div>
                                <span className={styles['sidebar-link-text']}>{row.label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>
                <ul className="flex flex-col mt-auto items-center">
                    <Avatar
                        data-cy="sidebar-client-logo"
                        src={client.logoUrl}
                        alt={client.name}
                        backgroundColor={client.preferences?.primaryColor}
                        size={40}
                    />

                    <div
                        className={[
                            'text-center text-xs text-grayscale-500 font-medium mt-2',
                            styles['sidebar-footer-title'],
                        ].join(' ')}
                        data-cy={`sidebar-client-${client.name}`}
                    >
                        {client.name}
                    </div>
                    <div className="text-center text-[8px] text-grayscale-500 font-medium mt-1">Dashboard</div>
                    <div
                        className={clsx(
                            hasProPlan ? 'text-[#0052CC]' : 'text-warning-500',
                            'text-center text-[10px]  font-semibold'
                        )}
                    >
                        {hasProPlan ? 'PRO' : 'CORE'}
                    </div>
                </ul>
            </div>
        </nav>
    );
};

export default Sidebar;
